import TrustCards from 'components/Pages/TrustFinder/TrustCards';
import { useGetTrustArticlesQuery } from '@/store/articles/services/articleAPI';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import TrustFinderFilterBar from 'components/Pages/TrustFinder/TrustFinderFilterBar';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getArticlesData } from '@/store/articles';

const TrustFinder = ({ rendering, fields }): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { appliedFilters } = useSelector(getArticlesData);
  const { data, isFetching } = useGetTrustArticlesQuery({ language: sitecoreContext.language });

  const filteredArticles = useMemo(() => {
    return (
      data
        ?.filter((article) => {
          return appliedFilters.every((filter) => {
            if (!filter.values.length) return true;

            const articleValue = article[filter.name]?.targetItem?.name?.value;
            return filter.values.includes(articleValue);
          });
        })
        .toSorted((a, b) => a.trustName.value.localeCompare(b.trustName.value)) ?? []
    );
  }, [data, appliedFilters]);

  const filterOptions = useMemo(() => {
    const createOptions = (items, key) => {
      const uniqueOptions = [...new Set(items?.map((item) => item[key]?.targetItem?.name?.value))];

      return uniqueOptions.map((option) => {
        const aggregateCount = filteredArticles.filter(
          (item) => item[key].targetItem.name.value === option
        )?.length;
        const isOptionApplied = appliedFilters.some(
          (f) => f.name === key && f.values.includes(option)
        );
        return {
          aggregateCount: aggregateCount,
          applied: isOptionApplied,
          count: 0,
          disabled: aggregateCount === 0,
          selected: isOptionApplied,
          showAggregateCount: aggregateCount !== 0,
          text: option,
        };
      });
    };

    return {
      assetClassOptions: createOptions(data, 'trustAssetClass'),
      regionsOptions: createOptions(data, 'trustRegion'),
      styleOptions: createOptions(data, 'trustStyle'),
    };
  }, [filteredArticles, appliedFilters, data]);

  const filters = useMemo(() => {
    return fields.items.map((filter) => ({
      displayName: filter.fields.displayName.value,
      label: filter.fields.displayName.value,
      displayNameKey: '',
      id: filter.fields.facetName.value,
      name: filter.fields.facetName.value,
      options:
        filter.fields.facetName.value === 'trustAssetClass'
          ? filterOptions.assetClassOptions
          : filter.fields.facetName.value === 'trustRegion'
          ? [...filterOptions.regionsOptions]
          : [...filterOptions.styleOptions],
    }));
  }, [filterOptions, fields.items]);

  return (
    <section>
      <TrustFinderFilterBar
        e2eID={rendering.uid}
        formattedFilters={filters}
        filterBarSubHeading={fields.filterSubHeading?.value}
      />
      {!isFetching && (
        <TrustCards
          data-e2e-component-uid={`${rendering.uid}-listing'`}
          articles={filteredArticles}
        />
      )}
    </section>
  );
};

export default TrustFinder;
